import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';

/**
 * Shared CRM modal — indigo-consistent design.
 *
 * Props:
 *   open           — boolean
 *   onClose        — function
 *   title          — string | ReactNode
 *   children       — dialog body (preferred over content)
 *   content        — alias for children (legacy)
 *   actions        — ReactNode rendered in DialogActions
 *   maxWidth       — 'xs' | 'sm' | 'md' | 'lg' | 'xl' (default 'sm')
 *   fullWidth      — boolean (default true)
 *   contentProps   — extra props forwarded to DialogContent
 */
const Modal = ({
  open,
  onClose,
  title,
  children,
  content,
  actions,
  maxWidth = 'sm',
  fullWidth = true,
  contentProps = {},
}) => {
  const body = children ?? content;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '14px',
          border: '1px solid #d8e6f3',
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,251,255,0.98) 100%)',
          boxShadow: '0 22px 60px rgba(15,23,42,0.18), 0 2px 8px rgba(15,23,42,0.08)',
          margin: 2,
          maxHeight: 'calc(100% - 32px)',
          fontFamily: "'Fira Sans', sans-serif",
          overflow: 'hidden',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(15,23,42,0.48)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      {title && (
        <DialogTitle
          sx={{
            px: 3,
            py: 1.8,
            borderBottom: '1px solid #e2edf7',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 800,
              color: '#0f172a',
              fontFamily: "'Fira Sans', sans-serif",
              letterSpacing: 0,
              pr: 2,
              lineHeight: 1.4,
            }}
          >
            {title}
          </Typography>
          <IconButton
            aria-label="Fermer"
            onClick={onClose}
            size="small"
            sx={{
              color: '#64748b',
              borderRadius: '8px',
              flexShrink: 0,
              border: '1px solid #dbe7f2',
              background: '#ffffff',
              boxShadow: '0 3px 10px rgba(15,23,42,0.06)',
              '&:hover': { background: '#eef6ff', color: '#075ba8', borderColor: '#a9cdec' },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
      )}

      <DialogContent
        {...contentProps}
        sx={{ px: 3, py: 2.5, background: '#ffffff', ...contentProps?.sx }}
      >
        {body}
      </DialogContent>

      {actions && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #e2edf7',
            background: '#f8fbff',
            gap: 1,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;
